import { WidgetButtonStyle } from '@enums/widget-button-style';
import { MessagesInteractionE, StatusE } from '@interfaces/flow.interface';
import { DefaultFieldsI } from './default-fields.interface';
import { ColorPickT } from '@omnitypes/color-pick.type';
import { PermissionUseEmailE } from '@enums/permission-use-email.enum';
import { ItemI } from '@components/inputs/autocomplete-chip-list/autocomplete-chip-list.component';
import { MsTeamsPairingStatusE, StartFlowStrategyE } from '@enums/channel.enum';

export type ChannelT =
  | 'sms'
  | 'widget'
  | 'whatsapp'
  | 'whatsapp-oficial'
  | 'external-chat'
  | 'facebook-messenger'
  | 'direct'
  | 'telegram'
  | 'internal'
  | 'all'
  | 'email'
  | 'email-ses'
  | 'linkedin'
  | 'reclame-aqui'
  | 'rcs'
  | 'ms-teams';

export interface ChannelI extends DefaultFieldsI {
  type: ChannelE;
  channel: WhatsappI | WidgetI | ChannelTypeEmailI | ChannelTypeSesProviderI | InstagramI | any;
  status: StatusE;
  needsReauth?: boolean;
  messageInteraction?: MessagesInteractionE;
  workerflow?: any;
  instanceKey?: string;
  webhook?: any;
  icon: string;
  class: string;
  omnismartInstance?: MessagesInteractionE;
  defaultTransferTeam?: string;
  autoSelectChannel?: MessagesInteractionE;
  groupInteraction?: MessagesInteractionE;
  sendAgentOnMessage?: MessagesInteractionE;
  isDeleted?: boolean;
  updatedBy?: string;
  /**
   * Somente leitura: URL pública do pacote `.zip` do app do Microsoft Teams, carimbada
   * pelo serviço de canais a partir da env do ambiente. Nunca é enviada de volta no save.
   */
  appPackageUrl?: string;
}

export interface WidgetFormFieldConfigurationI {
  label: string;
  enabled: boolean;
  required: boolean;
}

export interface WidgetFormConfigurationI {
  name: WidgetFormFieldConfigurationI;
  email: WidgetFormFieldConfigurationI;
  telephone: WidgetFormFieldConfigurationI;
  cpf_cnpj?: WidgetFormFieldConfigurationI;
}

export enum WidgetVariableSourceE {
  STATIC = 'static',
  PAGE = 'page',
}

export interface WidgetVariableI {
  name: string;
  label?: string;
  source: WidgetVariableSourceE;
  defaultValue?: string;
}

export interface WidgetEnrichmentOutputI {
  name: string;
  jsonata?: string;
}

export interface WidgetEnrichmentI {
  enabled: boolean;
  mapResponse?: boolean;
  webhook: {
    url: string;
    method: string;
    timeout?: number;
    outputVariables?: WidgetEnrichmentOutputI[];
    [key: string]: any;
  };
}

export interface WidgetVariablesConfigI {
  variables?: WidgetVariableI[];
  enrichment?: WidgetEnrichmentI;
}

export interface WidgetI extends DefaultFieldsI {
  channel: {
    name: string;
    buttonStyle: WidgetButtonStyle;
    virtualAgent: string;
    virtualAgentName: string;
    virtualAgentPhoto: string;
    virtualAgentPhotoName: string;
    virtualAgentPhotoSize: number;
    useForm: boolean;
    formFields: WidgetFormConfigurationI;
    position: PositionE;
    color: string;
    icons: IconsI[];
    lang: LangE;
    contactTime: string;
    formId: string;
    startStrategy?: StartFlowStrategyE;
    startFlow?: string;
    startAiAgent?: string;
    endFlow: string;
    formI: string;
    flow?: string;
    bspKey?: string;
    webhook?: string;
    useTemplateOnSteps?: boolean;
    useAutomaticMessage?: boolean;
    useAutomaticTemplate?: boolean;
    timeTemplateOnStep?: any;
    timeAutomaticMessage?: any;
    automaticMessage: string;
    automaticTemplate: string;
    config: any;
    variablesConfig?: WidgetVariablesConfigI;
  };
}

/**
 * Microsoft Teams channel payload. The connection is inverted compared to the other
 * channels: the customer pastes no credential — the Omnismart app is installed in THEIR
 * tenant and the binding is the tenant id itself, which is the OUTCOME of pairing (null
 * while PENDING), not a precondition for it. The screen therefore reads these fields and
 * never writes them.
 */
export interface MsTeamsI {
  name: string;
  tenantId?: string | null;
  activationCode?: string;
  codeExpiresAt?: string;
  pairingStatus?: MsTeamsPairingStatusE;
  pairedAt?: string;
  pairedBy?: MsTeamsPairedByI;
  allowedEmailDomains?: string[];
  startStrategy?: StartFlowStrategyE;
  startFlow?: string;
  startAiAgent?: string;
  endFlow?: string;
  icon?: string;
}

export interface MsTeamsPairedByI {
  aadObjectId?: string;
  name?: string;
  email?: string;
}

export interface WhatsappI {
  name: string;
  cellphone: string;
  provider: string;
  webhook: string;
  startStrategy?: StartFlowStrategyE;
  startFlow?: string;
  startAiAgent?: string;
  endFlow?: string;
  timeout?: number;
  countryCode?: string;
  additionalWeblinks: string[];
  webLink?: WhatsappWebLinkI;
  weblinkInstances?: WhatsappWebLinkI[];
  callEnabled?: boolean;
}

export interface WhatsappWebLinkI {
  _id?: string;
  name?: string;
  customerId?: string;
  size: number;
  position: PositionE;
  color: ColorPickT;
  message: string;
  useForm?: boolean;
  formFields?: WidgetFormConfigurationI;
  formId?: string;
  showPoweredBy?: boolean;
  subtitle?: string;
  supportingText?: string;
  virtualAgentName?: string;
  virtualAgentPhotoName?: string;
  virtualAgentPhoto?: string;
}

export interface FacebookI extends DefaultFieldsI {
  name: string;
  token: string;
  instance: string;
  lang: LangE;
  contactTime: string;
  startFlow: string;
  endFlow: string;
  pageId?: string;
}

export interface InstagramI {
  instagramProfileId: string;
  instagramProfileName: string;
  instagramProfileUsername: string;
  accessToken: string;
  timeout?: string;
  startFlow: string;
  endFlow: string;
  pageId?: string;
  commentKeywords?: InstagramCommentKeywordI[];
}

export interface InstagramCommentKeywordI {
  words: ItemI[];
  flow: string;
  media?: string;
  type: Array<'POST' | 'REELS'>;
}

export enum PositionE {
  TOP_RIGHT = 'top_right',
  TOP_LEFT = 'top_left',
  BOTTOM_RIGHT = 'bottom_right',
  BOTTOM_LEFT = 'bottom_left',
}

export interface IconsI {
  url: string;
  label: string;
}

export enum ChannelE {
  INTERNAL = 'internal',
  SMS = 'sms',
  WIDGET = 'widget',
  WHATSAPP = 'whatsapp',
  WHATSAPP_OFICIAL = 'whatsapp-oficial',
  EXTERNAL_CHAT = 'external-chat',
  FACEBOOK = 'facebook-messenger',
  INSTAGRAM = 'direct',
  TELEGRAM = 'telegram',
  EMAIL = 'email',
  EMAIL_SES = 'email-ses',
  LINKEDIN = 'linkedin',
  RECLAME_AQUI = 'reclame-aqui',
  RCS = 'rcs',
  MS_TEAMS = 'ms-teams',
}

export enum LangE {
  PT_BR = 'pt-BR',
  EN_US = 'en-US',
  ES_ES = 'es-ES',
}

export interface ChannelDisplaySelectI {
  name: string;
  value: string;
}

interface PermissionOption {
  label: string;
  isEnabled: boolean;
  permission: PermissionUseEmailE;
}

interface Config {
  permissionOptions?: PermissionOption[];
  startFlow?: string;
  endFlow?: string;
}

export interface ChannelTypeEmailI {
  name: string;
  provider: string;
  password: string;
  email: string;
  host?: string;
  port?: number;
  config?: Config;
}

export interface ChannelTypeSesProviderI {
  name: string;
  email: string;
  domain?: string;
  dkimKeys?: string[];
  dkimValidated?: boolean;
  mailAdressValidated?: boolean;
}
